import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { Button, Panel } from 'src/components/app-ui'
import URL from 'src/api/url/api'
import { getCollectionLandingPageService } from 'src/api/collection'
import { PatchData } from 'src/model/patch'
import { patchCollectionLandingPageAction } from 'src/reducers/collection/action'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import ContainerMediaGalery from 'src/container/image-gallery'
import safeTrack from 'src/utils/safe-track'
import useStyles from './styles'

// The save saga skips the callback on failure (it shows its own error).
const SAVE_TIMEOUT = 15000

const FIELDS = ['seoTitle', 'seoDescription', 'seoKeywords', 'thumbnail']

const mediaUrl = path =>
  !path ? '' : path.startsWith('http') ? path : `${URL.MEDIA}${path}`

/**
 * Portal › SEO & sharing: the home landing page's SEO, the same fields as
 * Landing page settings › SEO in its editor (title, description, keywords)
 * plus the social sharing image, picked from the media gallery. Changes are
 * saved together with Save.
 */
const SeoTab = ({ collection }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const timeout = useRef(null)
  // Saved values, and the edits not saved yet.
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    getCollectionLandingPageService(collection.id)
      .then(response => {
        if (cancelled) return
        const page = response?.data || {}
        const values = Object.fromEntries(
          FIELDS.map(field => [field, page[field] || '']),
        )
        setSaved(values)
        setDraft(values)
      })
      .catch(() => {
        if (cancelled) return
        enqueueSnackbar('Could not load the SEO settings.', {
          variant: 'error',
        })
      })
    return () => {
      cancelled = true
      clearTimeout(timeout.current)
    }
  }, [collection.id, enqueueSnackbar])

  const changes = saved
    ? FIELDS.map(name => [name, (draft[name] || '').trim()]).filter(
        ([name, value]) => value !== (saved[name] || ''),
      )
    : []

  const save = () => {
    if (!changes.length || saving) return
    setSaving(true)
    clearTimeout(timeout.current)
    timeout.current = setTimeout(() => setSaving(false), SAVE_TIMEOUT)
    const next = { ...saved, ...Object.fromEntries(changes) }
    dispatch(
      patchCollectionLandingPageAction(
        collection.id,
        changes.map(([path, value]) => ({
          op: PatchData.OPERATION_REPLACE,
          path,
          value: value || null,
        })),
        () => {
          clearTimeout(timeout.current)
          setSaving(false)
          setSaved(next)
          setDraft(next)
          safeTrack('saved_portal_seo', {
            fields_changed: changes.map(([name]) => name),
            has_title: Boolean(next.seoTitle),
            has_description: Boolean(next.seoDescription),
            has_keywords: Boolean(next.seoKeywords),
            has_social_image: Boolean(next.thumbnail),
          })
          enqueueSnackbar('Saved', { variant: 'success' })
        },
      ),
    )
  }

  const field = name => ({
    value: draft[name] ?? '',
    onChange: event =>
      setDraft(current => ({ ...current, [name]: event.target.value })),
  })

  const setImage = url => setDraft(current => ({ ...current, thumbnail: url }))

  const openGallery = () =>
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'Media Gallery - Social image',
          component: (
            <ContainerMediaGalery
              onOk={items => {
                if (items?.[0]?.url) setImage(items[0].url)
                dispatch(messageBoxHideAction('message-box-1'))
              }}
              setImageURL={() => {}}
            />
          ),
          variant: MessageBoxVariant.DRAGGABLE,
          maxHeight: '640px',
        }),
      ),
    )

  if (!saved) return null

  const image = mediaUrl(draft.thumbnail)

  return (
    <div className={classes.seoGrid}>
      <Panel className={classes.card}>
        <div className={classes.cardTitle}>Search &amp; sharing</div>
        <label className={classes.label}>
          Page title
          <input
            className={classes.input}
            maxLength={200}
            placeholder="e.g. Acme Academy · Learn the Acme way"
            {...field('seoTitle')}
          />
          <span className={classes.help}>
            Shown in the browser tab, search results and social share posts.
          </span>
        </label>
        <label className={classes.label}>
          Description
          <textarea
            className={classes.textarea}
            rows={3}
            maxLength={320}
            {...field('seoDescription')}
          />
          <span className={classes.help}>
            Appears in search results and social share posts. Max 320
            characters.
          </span>
        </label>
        <label className={classes.label}>
          Keywords
          <input
            className={classes.input}
            placeholder="keyword, keyword, keyword"
            {...field('seoKeywords')}
          />
          <span className={classes.help}>Separate keywords by commas.</span>
        </label>
        <div className={classes.label}>
          Social image
          <button
            type="button"
            className={classes.dropzone}
            style={{ width: '100%', fontFamily: 'inherit' }}
            onClick={openGallery}
          >
            {image ? (
              <img src={image} alt="" className={classes.dropzoneImage} />
            ) : (
              <>
                <Icon icon="tabler:photo" width={20} height={20} />
                Choose a 1200×630 image
              </>
            )}
          </button>
          {image && (
            <div className={classes.imageActions}>
              <button
                type="button"
                className={classes.linkButton}
                onClick={openGallery}
              >
                Replace
              </button>
              <button
                type="button"
                className={`${classes.linkButton} ${classes.linkDanger}`}
                onClick={() => setImage('')}
              >
                Remove
              </button>
            </div>
          )}
        </div>
        <div className={classes.seoActions}>
          <Button disabled={!changes.length || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Panel>
    </div>
  )
}

export default SeoTab
